// src/services/pftUploadService.js
//
// Uploads PFT report PDFs from the admin page straight into the PFT extraction
// job's private bucket (PFT_UPLOAD_BUCKET), under uploads/<tenant>/pft/.
// That bucket has its own storage rule (storage.pft.rules): create-only, PDFs
// only, admins with a valid 2-step session only. Nothing is read back.
//
// Separate from the portal's main storage (src/firebase.js `storage`), which is
// not touched here.

import { getApp } from "firebase/app";
import { getStorage, list, ref, uploadBytesResumable } from "firebase/storage";
import { PFT_UPLOAD_BUCKET } from "../config/featureFlags";
import { PARALLEL_UPLOADS, objectFileName, objectPath } from "./pftUploadPlan";

let _storage = null;
const pftStorage = () => {
  if (!PFT_UPLOAD_BUCKET) throw new Error("PFT upload bucket is not configured");
  if (!_storage) _storage = getStorage(getApp(), `gs://${PFT_UPLOAD_BUCKET}`);
  return _storage;
};

// Per-browser memory of what already went up in a batch, so picking the same
// files again skips them. A convenience only: if it is lost, a re-upload of an
// existing file is refused by the storage rule (create-only) and reported.
const manifestKey = (tenantId, batchId) => `pftUpload:${tenantId}:${batchId}`;

export const loadUploadedNames = (tenantId, batchId) => {
  try {
    return new Set(JSON.parse(localStorage.getItem(manifestKey(tenantId, batchId)) || "[]"));
  } catch {
    return new Set();
  }
};

const rememberUploaded = (tenantId, batchId, names) => {
  try {
    localStorage.setItem(manifestKey(tenantId, batchId), JSON.stringify([...names]));
  } catch {
    // storage unavailable (private window etc.): the rule still prevents overwrites
  }
};

/**
 * Upload files, PARALLEL_UPLOADS at a time.
 *   onFile({ name, status: "uploading" | "done" | "failed", reason? })
 *   onBytes(bytesDoneSoFar)
 *   shouldStop(): return true to stop starting new files (running ones finish)
 * Resolves to { done: [names], failed: [{ name, reason }] }. Never rejects.
 */
export const uploadPfts = async ({ files, tenantId, batchId, onFile, onBytes, shouldStop }) => {
  const storage = pftStorage();
  const uploaded = loadUploadedNames(tenantId, batchId);
  const done = [];
  const failed = [];
  const progress = new Map(); // file -> bytes transferred
  const report = () => onBytes?.([...progress.values()].reduce((a, b) => a + b, 0));
  const queue = [...files];

  const uploadOne = (file) =>
    new Promise((resolve) => {
      const name = objectFileName(file.name);
      onFile?.({ name, status: "uploading" });
      let task;
      try {
        task = uploadBytesResumable(ref(storage, objectPath(tenantId, batchId, file.name)), file, {
          contentType: "application/pdf",
        });
      } catch (err) {
        // Could not even start (e.g. an unusable file name). Report it and move on,
        // so one bad file can never leave the session stuck "waiting".
        const reason = "could not start this file";
        failed.push({ name, reason });
        onFile?.({ name, status: "failed", reason });
        resolve();
        return;
      }
      task.on(
        "state_changed",
        (snap) => {
          progress.set(file, snap.bytesTransferred);
          report();
        },
        (err) => {
          progress.set(file, 0);
          report();
          // "unauthorized" for a file that wasn't uploaded before usually means a file
          // with this name already exists (the rule refuses overwrites).
          const reason = err?.code === "storage/unauthorized"
            ? "a file with this name is already uploaded (if it is a different report, rename it and upload again)"
            : err?.code === "storage/canceled" ? "cancelled" : "network or server error";
          failed.push({ name, reason });
          onFile?.({ name, status: "failed", reason });
          resolve();
        },
        () => {
          progress.set(file, file.size);
          report();
          done.push(name);
          uploaded.add(name);
          rememberUploaded(tenantId, batchId, uploaded);
          onFile?.({ name, status: "done" });
          resolve();
        },
      );
    });

  const worker = async () => {
    while (queue.length && !shouldStop?.()) {
      const file = queue.shift();
      try {
        await uploadOne(file);
      } catch {
        // uploadOne already reports its own failures; this is a last guard so a
        // worker never stops early and leaves files unstarted.
        const name = objectFileName(file.name);
        failed.push({ name, reason: "unexpected error" });
        onFile?.({ name, status: "failed", reason: "unexpected error" });
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLEL_UPLOADS, queue.length) }, worker));
  return { done, failed };
};

/**
 * Names of every PDF already in the company's upload folder, from the bucket itself
 * (so it covers uploads from any computer). Names only: the storage rule allows
 * admins to LIST this folder but never to open or download a file.
 * Resolves to an array of stored names; rejects if listing isn't allowed.
 */
export const listUploadedNames = async (tenantId, folder) => {
  const storage = pftStorage();
  const names = [];
  let pageToken;
  do {
    // eslint-disable-next-line no-await-in-loop
    const page = await list(ref(storage, `uploads/${tenantId}/${folder}`), { maxResults: 1000, pageToken });
    names.push(...page.items.map((i) => i.name));
    pageToken = page.nextPageToken;
  } while (pageToken);
  return names;
};
